/**
 * changelogData.js
 *
 * Source of truth for all product changelogs. Edit this file to publish
 * release notes — the ChangelogPage component reads from here automatically.
 *
 * ─── STRUCTURE ────────────────────────────────────────────────────────────────
 *
 * Each product key maps to:
 *   productName   string    Display name shown in the page header
 *   entries       array     Ordered newest → oldest (top of page = latest)
 *
 * Each entry in `entries`:
 *   version   string    Semantic version label, e.g. "v1.2.0"
 *   date      string    Human-readable release date, e.g. "July 2026"
 *                       Leave "" if not yet released / not yet dated.
 *   title     string    Optional one-line release name, e.g. "Multi-utility rollout"
 *                       Leave "" to omit.
 *   added     Item[]    New features or capabilities introduced in this release.
 *   improved  Item[]    Enhancements, performance improvements, or UX refinements.
 *   fixed     Item[]    Bug fixes and resolved issues.
 *   notes     Item[]    Migration notes, deprecations, or other callouts.
 *
 * Item can be either:
 *   - a plain string  → rendered as a single bullet point
 *   - { section: string, items: string[] }
 *                     → renders a sub-section header with its own bullet list
 *                       (use this to group related changes under a named feature)
 *
 * Leave any section as an empty array [] if there is nothing to record for it.
 *
 * ─── VERSIONING CONVENTION ────────────────────────────────────────────────────
 *
 *   MAJOR (v1.0.0 → v2.0.0)  Breaking changes or complete feature overhauls
 *   MINOR (v0.1.0 → v0.2.0)  New features, backward-compatible additions
 *   PATCH (v0.1.0 → v0.1.1)  Bug fixes and minor tweaks
 *
 * ─── WORKFLOW ─────────────────────────────────────────────────────────────────
 *
 *   1. Add a new object to the TOP of the `entries` array (newest first).
 *   2. Fill in `version`, `date`, `title`, and the relevant change arrays.
 *   3. Leave sections empty ([]) if unused — they are hidden automatically.
 *   4. Commit to main; the page updates on next deploy.
 */

// ── eFleetPlan ────────────────────────────────────────────────────────────────

export const fleetChangelog = {
  productName: "eFleetPlan",
  entries: [
    {
      version: "v0.1.1",
      date: "November 2026",
      title: "Vehicle and charging data restored",
      added: [],
      improved: [
        "Adding a route or depot keeps the page usable and shows a short status note instead of locking the screen",
        "Energy rate details stay with the rate card, so they no longer cover the step bar",
      ],
      fixed: [
        "Signed-in users can load the shared vehicle catalog and charging stations in eFleetPlan again",
      ],
      notes: [
        "Latest Result was removed from the sidebar. Open a saved case from the Case Library.",
      ],
    },
    {
      version: "v0.1.0",
      date: "",
      title: "Initial Release",
      added: [
        {
          section: "Case Setup",
          items: [
            "Guided 6-step wizard for building fleet electrification cases (routes, depot charging, public charging, vehicles, energy rates, optimization settings)",
            "Optimization objective selector — minimize total cost of ownership, accelerate EV adoption, or minimize greenhouse-gas emissions — with optional upfront budget and electrification-target constraints",
          ],
        },
        {
          section: "Case Library",
          items: [
            "Case duplication for exploring scenario variations without re-entering inputs",
            "Multi-case comparison export to formatted PDF report",
          ],
        },
        {
          section: "AI Advisor",
          items: [
            "Context-aware Q&A on case drafts, results, or multi-case comparison via sidebar",
            "AI-generated PDF report from single case result or multi-case comparison",
          ],
        },
      ],
      improved: [],
      fixed: [],
      notes: [],
    },
  ],
};

// ── ChargerPlan ───────────────────────────────────────────────────────────────

export const chargerChangelog = {
  productName: "ChargerPlan",
  entries: [
    {
      version: "v0.2.0",
      date: "September 2026",
      title: "Current-year SCE capacity outlook",
      added: [
        "Circuit popups now show SCE’s multi-year Available Load Capacity outlook, A-Bank (subtransmission transformer) capacity, and serving-substation Grid Needs Assessment capacity",
      ],
      improved: [
        "Capacity heatmap and circuit colors use estimated available capacity for the current calendar year from the latest SCE DRPEP outlook, rather than the plan-year (year-1) value",
      ],
      fixed: [],
      notes: [],
    },
    {
      version: "v0.1.0",
      date: "",
      title: "Initial Release",
      added: [
        {
          section: "Parcel Examination",
          items: [
            "Interactive map for exploring California industrial land parcels with address search, viewport, and area-based queries",
            "Per-parcel attribute view: APN, land value, area (sq ft), county, zoning classification, utility region, and maximum nearby grid hosting capacity (kW)",
            "Toggleable data layers for geographic context and detailed parcel attributes"
          ],
        },
        {
          section: "Parcel Comparison",
          items: [
            "Sortable side-by-side table for comparing selected parcels by land value, area, zoning, utility region, and nearby grid hosting capacity",
            "Automatic highlight of highest-scoring recommended parcel",
            "Export comparison to Excel or PDF for sharing and offline review",
          ],
        },
        {
          section: "Search by Preferences",
          items: [
            "Weighted parcel ranking across 10 site-selection criteria with importance sliders (Not Important → Very Important)",
            "Apply or reset preferences at any time — results re-rank instantly against the current search",
          ],
        },
      ],
      improved: [],
      fixed: [],
      notes: [],
    },
  ],
};

// ── GridPlan ──────────────────────────────────────────────────────────────────

export const utilityChangelog = {
  productName: "GridPlan",
  entries: [
    {
      version: "v0.2.0",
      date: "September 2026",
      title: "California heavy-duty load forecast",
      added: [
        {
          section: "Published HD forecast",
          items: [
            "Statewide California heavy-duty truck charging load forecast for 2027–2030, with an interactive hex map, year selector, and hex-level energy and peak-power profiles",
            "Technical documentation for the forecast methodology",
            "Available to logged-in users from the Solution Hub (no GridPlan entitlement required)",
          ],
        },
        {
          section: "Case studies",
          items: [
            "California transit load case study in GridPlan, combining HD truck load with transit charging for entitled users",
          ],
        },
      ],
      improved: [],
      fixed: [],
      notes: [],
    },
  ],
};

// ── PolicyPlan ────────────────────────────────────────────────────────────────

export const regulatorChangelog = {
  productName: "PolicyPlan",
  entries: [
    {
      version: "v0.2.0",
      date: "September 2026",
      title: "A-Bank capacity assessment",
      added: [
        "A-Bank (SCE subtransmission transformer) name and current-year available capacity on location metrics",
        "Capacity assessment now compares planned deployment to the binding (lowest) of nearest-circuit, serving-substation, and A-Bank available capacity for the expected year",
      ],
      improved: [
        "Circuit, A-Bank, and substation outlook tables are anchored to the current calendar year",
        "Serving substations are matched to SCE Grid Needs Assessment names, with a unique base-name fallback when the full name does not match",
      ],
      fixed: [
        "Substation capacity import now reads the GNA Substations point layer, so outlook values attach to substations rather than circuit polylines",
      ],
      notes: [],
    },
  ],
};

// ── Data Hub ──────────────────────────────────────────────────────────────────

export const dataHubChangelog = {
  productName: "Data Hub",
  entries: [
    {
      version: "v0.1.0",
      date: "",
      title: "",
      added: [],
      improved: [],
      fixed: [],
      notes: [],
    },
  ],
};
